import type { MeasurementValueSummary } from "@/types/measurement";

/**
 * Which vendor owns a measurement value, when one does.
 *
 * The wire carries a boolean per vendor because the badge draws each source differently — its
 * own icon, colour and label. Read-only-ness does not vary that way, so it is derived from
 * this one discriminator rather than respelled at each of the places that hide an edit or
 * explain why it is hidden.
 */
export type MeasurementVendorSource = "inbody" | "lab" | "wearable";

type ProvenanceFlags = Pick<
  MeasurementValueSummary,
  "isLabSourced" | "isInbodySourced" | "isWearableSourced"
>;

const READ_ONLY_REASONS: Record<MeasurementVendorSource, string> = {
  inbody: "InBody scans are read-only",
  lab: "Lab results are read-only",
  wearable: "Device readings are read-only",
};

/** The vendor a value came from, or null when the clinic entered it by hand. */
export function vendorSource(
  value: ProvenanceFlags,
): MeasurementVendorSource | null {
  if (value.isInbodySourced) return "inbody";
  if (value.isLabSourced) return "lab";
  if (value.isWearableSourced) return "wearable";
  return null;
}

/**
 * Whether a value is owned by a vendor, and so cannot be edited or deleted here.
 *
 * The backend refuses both outright — see `VENDOR_SOURCES` in
 * `services/measurement/value.py` — so the client hides its Edit affordance to match rather
 * than offering one that returns a 400.
 */
export function isVendorSourced(value: ProvenanceFlags): boolean {
  return vendorSource(value) !== null;
}

/** Why a value cannot be edited by hand, or null when it can. */
export function vendorReadOnlyReason(value: ProvenanceFlags): string | null {
  const source = vendorSource(value);
  return source === null ? null : READ_ONLY_REASONS[source];
}
